import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import loadinggif from "../../assets/loading.gif";
import { process } from "@progress/kendo-data-query";
import { useProject } from "../../contexts/ProjectContext";
import { useEnvironmentConfigContext } from "../../contexts/EnvironmentConfigContext";
import { useHighlightedPoles } from "../../contexts/HighlightedPolesContext";
import { useQueryResults } from "../../contexts/QueryResultsContext";
import { useTabContext } from "../../contexts/ReportTabContext";
import { useUser } from "../../contexts/UserContext";
import SaveQueryPopup from "../../global_components/save_query_popup";

const min = new Date(new Date().setFullYear(new Date().getFullYear() - 1));


  const flattenObject = (obj, parentKey = "", result = {}) => {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (
          obj[key] &&
          typeof obj[key] === "object" &&
          !Array.isArray(obj[key])
        ) {
          flattenObject(obj[key], newKey, result);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  };

const CSReportPage = () => {
  const QUERY_PAGE = "CSReport";
  const REPORT_TYPE = "CS Report";

  const { user } = useUser();
  const {
    queryResults,
    setQueryResults,
    isQueryActive,
    activeQueryPayload,
    setActiveQueryPayload,
  } = useQueryResults();
  const { setHighlightedPoles } = useHighlightedPoles();
  const { selectedTab, setSelectedTab } = useTabContext();
  const excelExportRef = useRef(null);
  const [StartDateRemoval, setStartDateRemoval] = React.useState(null);
  const navigate = useNavigate();
  const [EndDateRemoval, setEndDateRemoval] = React.useState(null);
  const [StartDateAudit, setStartDateAudit] = React.useState(null);
  const [EndDateAudit, setEndDateAudit] = React.useState(null);
  const { selectedProject, GCP_Project, Dataset_ID } = useProject();
  const [dataState, setDataState] = React.useState({ skip: 0, take: 10 });
  const [allExpanded, setAllExpanded] = React.useState(false);
  const { environmentConfig } = useEnvironmentConfigContext();
  const [showSaveQueryPopup, setShowSaveQueryPopup] = React.useState(false);
  const [saveQueryOptions, setSaveQueryOptions] = React.useState(null);
  const [selectedQuery, setSelectedQuery] = React.useState(null);
  const [savedQueryList, setSavedQueryList] = React.useState([]);
  const [showWarning, setshowWarning] = React.useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = React.useState(false);
  const [saveQueryError, setSaveQueryError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [wizardContainer, setWizardContainer] = useState(null);

  const [dynamicSchema, setDynamicSchema] = React.useState([]);
  const [dynamicFormValues, setDynamicFormValues] = React.useState({});

  const [columnFilters, setColumnFilters] = useState({});
  const [searchedOptions, setSearchedOptions] = useState({});
  const searchDebounceTimer = useRef({});
  const [loadingColumns, setLoadingColumns] = useState({});

  const handleDynamicFieldChange = (columnName, value) => {
    setDynamicFormValues((prev) => ({
      ...prev,
      [columnName]: value,
    }));
  };

  const [dataResult, setDataResult] = React.useState(
    process(queryResults, dataState),
  );

  function isValidEntry(entry) {
    return entry !== null && entry !== undefined && entry !== "";
  }

  function isValidSubmit() {
    const hasDynamicValue = Object.values(dynamicFormValues).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      return val != null && val !== "";
    });

    return (
      hasDynamicValue ||
      isValidEntry(StartDateRemoval) ||
      isValidEntry(EndDateRemoval) ||
      isValidEntry(StartDateAudit) ||
      isValidEntry(EndDateAudit)
    );
  }

  function buildSelectedQueryOptions() {
    const dynamicFiltersPayload = {};
    Object.keys(dynamicFormValues).forEach((key) => {
      const val = dynamicFormValues[key];
      if (val instanceof Date) {
        const localDate = new Date(
          val.getTime() - val.getTimezoneOffset() * 60000,
        );
        dynamicFiltersPayload[key] = localDate.toISOString().split("T")[0];
      } else if (Array.isArray(val) && val.length > 0) {
        dynamicFiltersPayload[key] = val
          .map((item) => `"${String(item).trim()}"`)
          .join(",");
      } 
      else if (typeof val === "string" && val.trim() !== "") {
        dynamicFiltersPayload[key] = val
          .split(",")
          .map((item) => `"${item.trim()}"`)
          .filter((item) => item !== '""')
          .join(",");
      }
    });
    return {
      StartDateRemoval: StartDateRemoval ? StartDateRemoval.toISOString() : "",
      EndDateRemoval: EndDateRemoval ? EndDateRemoval.toISOString() : "",
      StartDateAudit: StartDateAudit ? StartDateAudit.toISOString() : "",
      EndDateAudit: EndDateAudit ? EndDateAudit.toISOString() : "",
      DynamicFilters: dynamicFiltersPayload
    };
  }

  function openSaveQueryPopup() {
    if (!isValidSubmit()) {
      setshowWarning(true);
      return;
    }

    setshowWarning(false);
    setSaveQueryError("");
    const options = buildSelectedQueryOptions();
    setSaveQueryOptions(options);
    setShowSaveQueryPopup(true);
  }

  function closeSaveQueryPopup() {
    setSaveQueryError("");
    setShowSaveQueryPopup(false);
  }

  function handleSaveQueryConfirm({ queryName, queryScope, queryOptions }) {
    const payloadToSave = {
      QueryName: queryName,
      QueryScope: queryScope,
      QueryPage: QUERY_PAGE,
      ReportType: REPORT_TYPE,
      Project: selectedProject,
      GCP_Project: GCP_Project,
      Dataset_ID: Dataset_ID,
      UserEmail: user?.email || "",
      QueryOptions: queryOptions,
    };

    setSaveQueryError("");

    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.text())
      .then((apiKey) => {
        return fetch(
          process.env.REACT_APP_WEB_API_URL + "/api-common/poles/SaveQuery",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ApiKey: apiKey,
              frontendenvironmentconfig: environmentConfig,
            },
            body: JSON.stringify(payloadToSave),
          },
        );
      })
      .then(async (res) => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(errorText || "Failed to save query.");
        }
        return res.json();
      })
      .then((data) => {
        setSaveQueryError("");
        setShowSaveQueryPopup(false);
        fetchSavedQueries();
        setShowSaveSuccess(true);
        setTimeout(() => {
          setShowSaveSuccess(false);
        }, 3000);
      })
      .catch((err) => {
        console.error("Error saving query:", err);

        let errorMessage = "Failed to save query.";

        if (err.message) {
          if (err.message.includes("already exists")) {
            errorMessage =
              "A saved query with this name already exists for this project.";
          } else {
            errorMessage = err.message;
          }
        }

        setSaveQueryError(errorMessage);
      });
  }

  function fetchSavedQueries() {
    if (!selectedProject || !Dataset_ID || !user?.email) return;

    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.text())
      .then((apiKey) => {
        return fetch(
          process.env.REACT_APP_WEB_API_URL +
            "/api-common/poles/GetSavedQueries",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ApiKey: apiKey,
              frontendenvironmentconfig: environmentConfig,
            },
            body: JSON.stringify({
              QueryPage: QUERY_PAGE,
              Project: selectedProject,
              Dataset_ID: Dataset_ID,
              UserEmail: user.email,
            }),
          },
        );
      })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch saved queries.");
        }
        return res.json();
      })
      .then((data) => {
        setSavedQueryList(data || []);
      })
      .catch((err) => {
        console.error("Error fetching saved queries:", err);
      });
  }

  function handleSavedQueryChange(e) {
    const selected = e.target.value;
    setSelectedQuery(selected);

    if (!selected) return;

    const options = selected.QueryOptions || {};

    resetQueryFormAndSwitchTab();

    const parsedDynamicFilters = {};

    if (options.DynamicFilters) {
      Object.keys(options.DynamicFilters).forEach((key) => {
        const val = options.DynamicFilters[key];
        if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
          parsedDynamicFilters[key] = new Date(
            val.includes("T") ? val : `${val}T00:00:00`,
          );
        } else if (
          typeof val === "string" &&
          (val.includes('"') || val.includes(","))
        ) {
          parsedDynamicFilters[key] = val
            .split(",")
            .map((item) => item.replace(/^["']|["']$/g, "").trim())
            .filter((item) => item !== "");
        } else if (
          typeof val === "string" &&
          val.startsWith('"') &&
          val.endsWith('"')
        ) {
          parsedDynamicFilters[key] = [val.replace(/^["']|["']$/g, "").trim()];
        } else {
          parsedDynamicFilters[key] = val;
        }
      });
    } else {
      parsedDynamicFilters["Pole Status"] = options.Pole_Status ? [options.Pole_Status] : [];
      parsedDynamicFilters["Pull Type"] = options.Pull_Type ? [options.Pull_Type] : [];
      parsedDynamicFilters["NJUNS"] = options.NJUNS ? [options.NJUNS] : [];
      parsedDynamicFilters["SPANS"] = options.SPANS ? [options.SPANS] : [];
      parsedDynamicFilters["Region"] = options.Region ? [options.Region] : [];
      parsedDynamicFilters["State"] = options.State ? [options.State] : [];
      parsedDynamicFilters["Pole Number"] = options.Pole_Number || "";
    }

    setDynamicFormValues(parsedDynamicFilters);
    setStartDateAudit(options.StartDateAudit ? new Date(options.StartDateAudit) : null);
    setEndDateAudit(options.EndDateAudit ? new Date(options.EndDateAudit) : null);
  }

  useEffect(() => {
    if (selectedProject) {
      populateDropDownData();
      if (!selectedQuery) {
      resetQueryFormAndSwitchTab();
      setSelectedQuery(null);
      }
      setDataResult(process([], dataState));
    }
    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedQuery]);

  useEffect(() => {
    if (selectedProject && Dataset_ID && user?.email) {
      fetchSavedQueries();
    } else {
      setSavedQueryList([]);
      setSelectedQuery(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, Dataset_ID, user?.email, environmentConfig]);

  useEffect(() => {
    const allRowsExpanded =
      queryResults.length > 0 && queryResults.every((item) => item.expanded);
    setAllExpanded(allRowsExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResults]);

  const expandChange = (event) => {
    const isExpanded =
      event.dataItem.expanded === undefined
        ? event.dataItem.aggregates
        : event.dataItem.expanded;
    event.dataItem.expanded = !isExpanded;
    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
  };

  const expandAllRows = () => {
    let updatedExpandStatus = !allExpanded;
    queryResults.map((item) => {
      item.expanded = updatedExpandStatus;
      return item;
    });

    setDataResult({
      ...dataResult,
      data: [...dataResult.data],
    });
    setAllExpanded(!allExpanded);
  };

  const viewInMap = () => {
    const highlightedPolesList = queryResults.map((item) => item.GlobalId);
    setHighlightedPoles(highlightedPolesList);
    navigate("/map");
  };

  const dataStateChange = (event) => {
    setDataResult(process(queryResults, event.dataState));
    setDataState(event.dataState);
  };

  const gridWidth = 600;

  const handleSelect = (e) => {
    setSelectedTab(e.selected);
  };

  const handleSubmit = () => {
    if (!isValidSubmit()) {
      setshowWarning(true);
      return
    } 
      setshowWarning(false);

      const dynamicFiltersPayload = {};
      Object.keys(dynamicFormValues).forEach((key) => {
        const val = dynamicFormValues[key];
        if (val instanceof Date) {
        const localDate = new Date(val.getTime() - (val.getTimezoneOffset() * 60000));
        dynamicFiltersPayload[key] = localDate.toISOString().split("T")[0];
        } else if (Array.isArray(val) && val.length > 0) {
          dynamicFiltersPayload[key] = val.join(",");
        } else if (typeof val === "string" && val.trim() !== "") {
          dynamicFiltersPayload[key] = val.trim();
        }
      });

      const newPayload = JSON.stringify({
        Pull_Type: "",
        StartDateRemoval: StartDateRemoval ? StartDateRemoval.toISOString() : "",
        EndDateRemoval: EndDateRemoval ? EndDateRemoval.toISOString() : "",
        StartDateAudit: StartDateAudit ? StartDateAudit.toISOString() : "",
        EndDateAudit: EndDateAudit ? EndDateAudit.toISOString() : "",
        Project: selectedProject,
        GCP_Project: GCP_Project,
        Dataset_ID: Dataset_ID,
        DynamicFilters: dynamicFiltersPayload,
      });

      setActiveQueryPayload(newPayload);
      onWizardSubmit(newPayload);

  };

  const exportData = useMemo(() => {
    let dataArray = [];
    queryResults.forEach((item, index) => {
      const mainRowData = flattenObject(item.fieldDict?.Poles || {});

      const mainRow = {
        "Row Type": `Pole Result ${index + 1}`,
        "Pole Number": mainRowData["Pole Number"] || "",
        "Removal Date": mainRowData["Removal Date"] || "",
        "Audit Date": mainRowData["Audit Date"] || "",
      };
      dataArray.push(mainRow);

      if (item.attachmentsDict) {
        Object.values(item.attachmentsDict).forEach(
          (attacher, attacherIndex) => {
            const attacherData = flattenObject(attacher);
            const attacherRow = {
              "Row Type": `Attacher Information ${attacherIndex + 1}`,
              ...attacherData,
            };
            dataArray.push(attacherRow);
          },
        );
      }

      if (item.fieldDict?.Location_Details) {
        const locationData = flattenObject(item.fieldDict.Location_Details);
        const locationRow = {
          "Row Type": "Location Details",
          ...locationData,
        };
        dataArray.push(locationRow);
      }

      if (item.fieldDict?.Poles) {
        const poleInfoData = flattenObject(item.fieldDict.Poles);
        const poleInfoRow = {
          "Row Type": "Pole Information",
          ...poleInfoData,
        };
        dataArray.push(poleInfoRow);
      }
    });
    return dataArray;
  }, [queryResults]);

  const columns = useMemo(() => {
    const predefinedColumns = [
      "Row Type",
      "Pole Number",
      "Removal Date",
      "Audit Date",
    ];

    const allColumnKeys = new Set();
    exportData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        allColumnKeys.add(key);
      });
    });

    const additionalColumns = Array.from(allColumnKeys).filter(
      (key) => !predefinedColumns.includes(key),
    );
    const allColumns = predefinedColumns.concat(additionalColumns);

    return allColumns.map((key) => ({
      field: key,
      title: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    }));
  }, [exportData]);

  const populateDropDownData = () => {
    setLoading(true);
    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      },
    )
      .then((res) => {
        return res.text();
      })
      .then((apiKey) => {
        fetch(
          process.env.REACT_APP_WEB_API_URL +
            "/api-common/poles/GetDynamicQuerySchema",
          {
            method: "POST",
            headers: {
              frontendenvironmentconfig: environmentConfig,
              "Content-Type": "application/json",
              ApiKey: apiKey,
            },
            body: JSON.stringify({
              rawQuery: "",
              Project: selectedProject,
              GCP_Project: GCP_Project,
              Dataset_ID: Dataset_ID,
              Type: "", 
              Pull_Type: "",
              DynamicFilters: {}
            }),
          },
        )
          .then((res) => (res.ok ? res.json() : []))
          .then((schemaData) => {
            if (Array.isArray(schemaData)) {
              setDynamicSchema(schemaData);
            }
            setLoading(false);
          });
      });
  };

  const onWizardSubmit = (payload) => {
    fetch(
      process.env.REACT_APP_WEB_API_URL +
        "/api-common/Secrets/GetNimblyWebAPIKey",
      {
        method: "GET",
        headers: {
          frontendenvironmentconfig: environmentConfig,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.text())
      .then((apiKey) => {
        setLoading(true);
        fetch(
          process.env.REACT_APP_WEB_API_URL +
            "/api-common/poles/TestGetPulsePoles",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ApiKey: apiKey,
              frontendenvironmentconfig: environmentConfig,
            },
            body: payload,
          },
        )
          .then(async (res) => {
             if (!res.ok) {
                 const errText = await res.text();
                 throw new Error(errText);
             }
             return res.json();
          })
          .then((data) => {
            const flattenedData = data.map((item) => ({
              GlobalId: item.GlobalId,
              fieldDict: item.fieldDict,
              attachmentsDict: item.attachmentsDict,
              photosDict: item.photosDict,
              highlighted: false,
            }));

            setSelectedTab(1);
            setQueryResults(flattenedData);
            setHighlightedPoles([]);
            setLoading(false);
            setStartDateRemoval(null);
            setEndDateRemoval(null);
            setStartDateAudit(null);
            setEndDateAudit(null);
            setDynamicFormValues({});
            setSelectedQuery(null);
            setDataState({ skip: 0, take: 10 });
            setAllExpanded(false);
          })
          .catch((err) => {
            console.error("Backend Error:", err);
            setLoading(false);
          });
      });
  };

  useEffect(() => {
    setDataResult(process(queryResults, dataState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResults]);

  const setPercentage = (percentage) => {
    return Math.round(gridWidth / 100) * percentage;
  };

  const resetQueryFormAndSwitchTab = () => {
    setStartDateRemoval(null);
    setEndDateRemoval(null);
    setStartDateAudit(null);
    setEndDateAudit(null);
    setDynamicFormValues({});
  };

  const clearAllQueryFields = () => {
    resetQueryFormAndSwitchTab();
    setSelectedQuery(null);
    setActiveQueryPayload("");
  };

  const handleFilterChange = (columnName, filterValue) => {
      const safeText = (filterValue ?? "").toString();
  
      setColumnFilters((prev) => ({
        ...prev,
        [columnName]: safeText,
      }));

      if (searchDebounceTimer.current[columnName]) {
      clearTimeout(searchDebounceTimer.current[columnName]);
    }
  
    const trimmed = safeText.trim();

    const isGlobalIdCol = columnName.toLowerCase().includes("globalid");
    const minLengthRequired = isGlobalIdCol ? 4 : 2;

    if (trimmed.length < minLengthRequired) {
      setSearchedOptions((prev) => ({
        ...prev,
        [columnName]: [],
      }));
      setLoadingColumns((prev) => ({ ...prev, [columnName]: false }));
      return;
    }

    const waitTime = trimmed.length > 8 ? 300 : 950;

     setLoadingColumns((prev) => ({ ...prev, [columnName]: true }));

          searchDebounceTimer.current[columnName] = setTimeout(() => {
           fetch(
                   process.env.REACT_APP_WEB_API_URL +
                     "/api-common/Secrets/GetNimblyWebAPIKey",
                   {
                     method: "GET",
                     headers: {
                       frontendenvironmentconfig: environmentConfig,
                       "Content-Type": "application/json",
                     },
                   }
                 )
                   .then((res) => res.text())
                   .then((apiKey) => {
                     return fetch(
                       process.env.REACT_APP_WEB_API_URL +
                         "/api-common/poles/SearchColumnOptions",
                       {
                         method: "POST",
                         headers: {
                           frontendenvironmentconfig: environmentConfig,
                           "Content-Type": "application/json",
                           ApiKey: apiKey,
                         },
                         body: JSON.stringify({
                           GCP_Project: GCP_Project,
                           TableName: "Poles",
                           ColumnName: columnName,
                           SearchText: trimmed,
                           Type: "",
                         }),
                       }
                     )
                          })
                    .then(async (res) => {
                     if (!res.ok) {
                       const errorText = await res.text();
                       throw new Error(errorText || "Failed to search column options.");
                     }
                     return res.json();
                   })
                       .then((matches) => {
                         if (!Array.isArray(matches)) return;
                         setSearchedOptions((prev) => ({
                           ...prev,
                           [columnName]: matches
                             .filter((m) => m != null && m !== "")
                             .map(String),
                         }));
                       })
                       .catch((err) => {
                         console.error("Error looking for options:", err);
                         setSearchedOptions((prev) => ({
                           ...prev,
                           [columnName]: [],
                         }));
                       })
                       .finally(() => {
                         setLoadingColumns((prev) => ({ ...prev, [columnName]: false })); 
                   })
               }, waitTime);
             };
  
  const DetailComponent = (props) => {
    const dataItem = props.dataItem;
    const c = [1, 2, 3, 4];
    let last = 3;
    let first = 0;
    const picCol =
      dataItem.photosDict && Object.keys(dataItem.photosDict).length > 0
        ? c[first++]
        : c[last--];
    const attacherCol =
      dataItem.attachmentsDict &&
      Object.keys(dataItem.attachmentsDict).length > 0
        ? c[first++]
        : c[last--];
    const locationCol = c[first++];
    const infoCol = c[first++];

    const renderFieldItems = (fields) => {
      return Object.entries(fields).map(([key, value], index) => (
        <React.Fragment key={index}>
          <GridLayoutItem
            className="pulse-queryresult-expanded-detail-column"
            row={index + 2}
            col={1}
          >
            {key.replace(/_/g, " ")}:
          </GridLayoutItem>
          <GridLayoutItem row={index + 2} col={2}>
            {value}
          </GridLayoutItem>
        </React.Fragment>
      ));
    };

    return (
      <GridLayout
        gap={{
          rows: 2,
          cols: 4,
        }}
        rows={[
          {
            height: 250,
          },
        ]}
        cols={[
          {
            width: "25%",
          },
          {
            width: "25%",
          },
          {
            width: "25%",
          },
          {
            width: "25%",
          },
        ]}
      >
        {dataItem.photosDict && Object.keys(dataItem.photosDict).length > 0 ? (
          <GridLayoutItem className="pulse-img-container" row={1} col={picCol}>
            {dataItem.photosDict &&
              Object.values(dataItem.photosDict).map((photo, x) => (
                <a
                  href={photo["Photo Path"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={x}
                >
                  <img
                    className="pulse-img"
                    src={photo["Photo Path"]}
                    alt={`Item ${x + 1}`}
                  />
                </a>
              ))}
          </GridLayoutItem>
        ) : null}

        {dataItem.attachmentsDict &&
        Object.keys(dataItem.attachmentsDict).length > 0 ? (
          <GridLayoutItem
            className="pulse-expand-content pulse-img-container"
            row={1}
            col={attacherCol}
          >
            {Object.entries(dataItem.attachmentsDict).map(
              ([attacherKey, attacher], x) => (
                <GridLayout
                  gap={{
                    rows: 8,
                    cols: 2,
                  }}
                  cols={[
                    {
                      width: "49%",
                    },
                    {
                      width: "49%",
                    },
                  ]}
                  key={x}
                >
                  <GridLayoutItem
                    className="pulse-expand-title"
                    row={1}
                    col={1}
                    colSpan={2}
                  >
                    Attacher Information {x + 1}
                  </GridLayoutItem>
                  {renderFieldItems(attacher)}
                </GridLayout>
              ),
            )}
          </GridLayoutItem>
        ) : null}

        <GridLayoutItem
          className="pulse-expand-content pulse-img-container"
          row={1}
          col={locationCol}
        >
          <GridLayout
            gap={{
              rows: 8,
              cols: 2,
            }}
            cols={[
              {
                width: "49%",
              },
              {
                width: "49%",
              },
            ]}
          >
            <GridLayoutItem
              className="pulse-expand-title"
              row={1}
              col={1}
              colSpan={2}
            >
              Location Details
            </GridLayoutItem>
            {renderFieldItems(dataItem.fieldDict.Location_Details)}
          </GridLayout>
        </GridLayoutItem>

        <GridLayoutItem
          className="pulse-expand-content pulse-img-container"
          row={1}
          col={infoCol}
        >
          <GridLayout
            gap={{
              rows: 14,
              cols: 2,
            }}
            cols={[
              {
                width: "49%",
              },
              {
                width: "49%",
              },
            ]}
          >
            <GridLayoutItem
              className="pulse-expand-title"
              row={1}
              col={1}
              colSpan={2}
            >
              Pole Information
            </GridLayoutItem>
            {renderFieldItems(dataItem.fieldDict.Poles)}
          </GridLayout>
        </GridLayoutItem>
      </GridLayout>
    );
  };

  return (
    <div className="ken-nimbly-page-content-wrapper">
      {loading ? (
        <div className="loading-icon-container">
          <img className="nimbly2-loader-gif" src={loadinggif} alt="Loading" />{" "}
        </div>
      ) : null}
      <TabStrip selected={selectedTab} onSelect={handleSelect}>
        <TabStripTab title="QUERY WIZARD">
          <p className="ken-nimbly-ov-main-title">Query Wizard</p>
          <div className="pulse-wizard-container">
            <div className="pulse-wizard-container-header">Generate Query</div>
            <div
              className={
                !showWarning
                  ? "pulse-warning-no-display"
                  : "pulse-wizard-container-warning"
              }
            >
              Please fill in at least one field to continue
            </div>
            <div
              className={
                !showSaveSuccess
                  ? "pulse-warning-no-display"
                  : "pulse-wizard-container-success"
              }
            >
              Query saved successfully
            </div>

            <div className="pulse-wizard-container-2" ref={setWizardContainer}>
              <GridLayout
                gap={{
                  rows: 4,
                  cols: 4,
                }}
              >
                <GridLayoutItem row={1} col={1} colSpan={4}>
                  <p className="Pulse-report-form-title">APPLY A SAVED QUERY</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>
                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={2}
                  col={1}
                >
                  Query:
                </GridLayoutItem>
                <GridLayoutItem row={2} col={2} colSpan={3}>
                  <DropDownList
                    data={savedQueryList}
                    textField="QueryName"
                    dataItemKey="Id"
                    popupSettings={{appendTo: wizardContainer, collision: {vertical: "none"}}}
                    value={selectedQuery}
                    onChange={(e) => handleSavedQueryChange(e)}
                  />
                </GridLayoutItem>
                <GridLayoutItem row={3} col={1} colSpan={4}>
                  <p className="Pulse-report-form-title">REMOVAL DATE</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>
                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={4}
                  col={1}
                >
                  Start Date:
                </GridLayoutItem>
                <GridLayoutItem row={4} col={2}>
                  <DatePicker
                    min={min}
                    value={StartDateRemoval}
                    placeholder="Choose a date..."
                    onChange={(e) => setStartDateRemoval(e.value)}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={4}
                  col={3}
                >
                  End Date:
                </GridLayoutItem>
                <GridLayoutItem row={4} col={4}>
                  <DatePicker
                    min={min}
                    value={EndDateRemoval}
                    placeholder="Choose a date..."
                    onChange={(e) => setEndDateRemoval(e.value)}
                  />
                </GridLayoutItem>
                <GridLayoutItem row={5} col={1} colSpan={4}>
                  <p className="Pulse-report-form-title">AUDIT DATE</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>
                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={6}
                  col={1}
                >
                  Start Date:
                </GridLayoutItem>
                <GridLayoutItem row={6} col={2}>
                  <DatePicker
                    min={min}
                    value={StartDateAudit}
                    placeholder="Choose a date..."
                    onChange={(e) => setStartDateAudit(e.value)}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  className="pulse-querywizard-column-text"
                  row={6}
                  col={3}
                >
                  End Date:
                </GridLayoutItem>
                <GridLayoutItem row={6} col={4}>
                  <DatePicker
                    min={min}
                    value={EndDateAudit}
                    placeholder="Choose a date..."
                    onChange={(e) => setEndDateAudit(e.value)}
                  />
                </GridLayoutItem>
              </GridLayout>

              <GridLayout
                gap={{
                  rows: 8,
                  cols: 2,
                }}
                cols={[{ width: 150 }]}
              >
                <GridLayoutItem row={1} col={1} colSpan={7}>
                  <p className="Pulse-report-form-title"> PROJECT INFO</p>
                  <div className="box-divider"></div>
                </GridLayoutItem>

                {Array.isArray(dynamicSchema) &&
                  dynamicSchema.map((field, index) => {
                    const rowNumber = 2 + index;
                    const colName = field.columnName || field.ColumnName || "";
                    const isNumber =
                      field.isNumberField || field.IsNumberField || false;
                    const isDate =
                      field.isDateField ||
                      field.IsDateField ||
                      colName.toLowerCase().includes("date");
                  const colOptions = (field.options || field.Options || [])
                      .filter((o) => o != null && o !== "")
                      .map(String);


                      const rawSelected = dynamicFormValues[colName];
                    const selectedArray = Array.isArray(rawSelected)
                      ? rawSelected
                      : typeof rawSelected === "string" &&
                          rawSelected.trim() !== ""
                        ? rawSelected
                            .split(",")
                            .map((v) => v.replace(/^["']|["']$/g, "").trim())
                            .filter(Boolean)
                        : [];
                    const selectedValues = selectedArray
                      .filter((v) => v != null && v !== "")
                      .map(String);


                    const searchText = (columnFilters[colName] || "").trim().toLowerCase();

                    const isSearching = searchText.length >= 2;
                    const isFetching = !!loadingColumns[colName];
                    let activeOptions = colOptions;

                    if (isSearching) {
                    const remoteResults = searchedOptions[colName];
                    if (remoteResults && remoteResults.length > 0) {
                      activeOptions = remoteResults.filter((opt) => opt.toLowerCase().includes(searchText));
                    } else {
                      activeOptions = colOptions.filter((opt) => opt.toLowerCase().includes(searchText));
                    }
                  }
     const displayedOptions = activeOptions.filter(opt => !selectedValues.includes(opt));

                  const noDataMessage = isFetching ? "Searching..." : "No data found";

                    return (
                      <React.Fragment key={colName}>
                        <GridLayoutItem
                          className="pulse-querywizard-column-text"
                          row={rowNumber}
                          col={1}
                        >
                          {colName.replace(/_/g, " ")}:
                        </GridLayoutItem>
                        <GridLayoutItem row={rowNumber} col={2} colSpan={6}>
                          {isDate ? (
                          <DatePicker
                            value={dynamicFormValues[colName] || null}
                            onChange={(e) => handleDynamicFieldChange(colName, e.value)}
                            placeholder="Choose a date..."
                          />
                        ) : isNumber ? (
                            <Input
                              value={dynamicFormValues[colName] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(colName, e.value)
                              }
                              placeholder="Ej: 123, 456"
                            />
                          ) : (
                            <MultiSelect
                              data={displayedOptions}
                              value={selectedValues}
                              popupSettings={{appendTo: wizardContainer, collision: {vertical: "none"}}}
                              onChange={(e) => {
                                handleDynamicFieldChange(colName, e.value || []);
                                setColumnFilters((prev) => ({ ...prev, [colName]: "" }));
                                setSearchedOptions((prev) => ({ ...prev, [colName]: [] }));
                              }}
                              filterable={true}
                              
                              onFilterChange={(e) => handleFilterChange(colName, e.filter?.value ?? "")}
                              placeholder="Select an option..."
                              listNoDataRender={() => (
                                <div style={{ padding: "8px", textAlign: "center", color: "#666" }}>
                                {noDataMessage}
                              </div>
                              )}
                            />
                          )}
                        </GridLayoutItem>
                      </React.Fragment>
                    );
                  })}

                <GridLayoutItem
                  className="pulse-textalignment-center"
                  row={3 + (dynamicSchema?.length || 0)}
                  col={2}
                >
                  <Button
                    type={"button"}
                    className="pulse-wizard-save-button"
                    onClick={openSaveQueryPopup}
                  >
                    Save Query
                  </Button>

                  <Button
                    type={"button"}
                    className="pulse-wizard-save-button"
                    onClick={clearAllQueryFields}
                  >
                    Clear All
                  </Button>

                  <Button
                    themeColor={"primary"}
                    type={"submit"}
                    className="pulse-wizard-submit-button"
                    onClick={handleSubmit}
                  >
                    Execute Query
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </div>
          </div>
        </TabStripTab>

        <TabStripTab title="QUERY REPORT">
          <div className="pulse-query-wizard-top-row">
            <p className="ken-nimbly-ov-main-title">Query Report</p>
            <div className="report-action-btn-group">
              <Button
                onClick={() => onWizardSubmit(activeQueryPayload)}
                disabled={!isQueryActive}
                className="pulse-expand-collapse-report-button"
              >
                <span>Get Latest Data</span>
              </Button>
              <Button
                onClick={() => viewInMap()}
                disabled={!isQueryActive}
                className="pulse-expand-collapse-report-button"
              >
                <span>View Results in Map</span>
              </Button>
              <Button
                onClick={() => expandAllRows()}
                disabled={queryResults.length === 0}
                className="pulse-expand-collapse-report-button"
              >
                <span>{allExpanded ? "Collapse" : "Expand"} All</span>
              </Button>
            </div>
          </div>

          <div>
            <div className="pulse-wizard-container-header">Query Results</div>
            <ExcelExport
              ref={excelExportRef}
              data={exportData}
              columns={columns}
              fileName={`QueryResults_${new Date().toISOString()}.xlsx`}
              onWorkbookCreate={(e) => {
                e.workbook.sheets.forEach((sheet) => {
                  sheet.rows.forEach((row) => {
                    if (row.type === "data") {
                      row.cells.forEach((cell) => {
                        if (typeof cell.value === "string" && cell.value.startsWith("=")) {
                          cell.formula = cell.value.substring(1);
                          cell.value = null;
                        }
                      });
                    }
                  });
                });
              }}
            />
            <Grid
              data={dataResult}
              {...dataState}
              onDataStateChange={dataStateChange}
              detail={DetailComponent}
              style={{
                height: "100%",
              }}
              expandField="expanded"
              onExpandChange={expandChange}
              filterable={true}
              pageable={{
                buttonCount: 4,
                pageSizes: [5, 10, 15, 20],
              }}
            >
              <Column
                field="fieldDict.Poles.Pole Number"
                title="Pole Number"
                width={setPercentage(55)}
              />
              <Column
                field="fieldDict.Poles.Removal Date"
                title="Removal Date"
                width={setPercentage(55)}
              />
              <Column
                field="fieldDict.Poles.Audit Date"
                title="Audit Date"
                width={setPercentage(55)}
              />
            </Grid>
          </div>
        </TabStripTab>
      </TabStrip>

      {showSaveQueryPopup && (
        <SaveQueryPopup
          queryOptions={saveQueryOptions}
          saveError={saveQueryError}
          onClose={closeSaveQueryPopup}
          onConfirm={handleSaveQueryConfirm}
        />
      )}
    </div>
  );
};

export default CSReportPage;
